import { fabric } from 'fabric';
import Cache from './common/Cache';
import Store from './common/Store';
import GraphicFactory from './components/GraphicFactory';
import { CanvasEventType } from './constants/Constants';
import Items from './manager/Items';
import Stage from './manager/Stage';
import Tools from './manager/Tools';
import UndoManager from './manager/UndoManager';
import Receiver from './net/Receiver';
import Sender from './net/Sender';
import log from 'loglevel';
import { setObjectCanSelect, setObjectDefaultControlOption } from './utils/utils';
import DocModuler from '../DocModuler';
import setWatermark from './watermak';
import DeferQueue from '../utils/DeferQueue';
window.UndoManager = UndoManager;
const deferQueue = new DeferQueue(100);
const maxDpr = 1.6; // 限制最高1.6倍Retina
fabric.devicePixelRatio = Math.min(fabric.devicePixelRatio, window.devicePixelRatio, maxDpr);

/**
 * 涂鸦基础服务
 *
 * @export
 * @class Doodle
 */
export default class Doodle {
  constructor() {
    // 缓存上一次 Canvas内部绘图缓冲区的宽高,而不是 DOM 元素的显示宽高
    this.lastCanvasSize = {
      width: 0,
      height: 0
    };
  }

  /**
   * 初始化
   *
   * @param {any} id dom中 canvas的 id
   * @param {any} options 对应的宽高设置
   * @memberof Doodle
   */
  init(id, options) {
    log.info(`init, canvasId: ${id}, ver:${this.version}`);
    log.info('[Doodle] init cost');
    let controlOpt = options.control;
    let advanceOpt = options.advance;
    let basicOpt = options.basic;
    this.canvas = new fabric.Canvas(
      id,
      Object.assign(
        {
          selection: false,
          enableRetinaScaling: !!basicOpt?.enableRetinaScaling, // 视网膜屏设置
          fireRightClick: false
          // backgroundColor: "#ff0000"
        },
        basicOpt
      )
    );

    let { editable, cacheable } = advanceOpt;

    this.store = new Store();
    const { width, height } = this.canvas;

    this.store.setKV('ow', width);
    this.store.setKV('oh', height);
    // 将canvas 挂在到window下
    if (!window.canvas) {
      window.canvas = [];
    }
    window.canvas.push(this.canvas);

    log.info('初始化舞台管理器');
    this.stage = new Stage(this.canvas);

    this._mouseEnable = editable || true;

    log.info('初始化缓存数据');
    this.cache = new Cache();
    this.cacheable = cacheable || false;

    log.info('初始化高级设置');
    log.info('初始化控制栏');
    this.tools = new Tools();
    this.tools.app = this;
    this.tools.defaultControlOption = controlOpt;
    log.info('初始化UNDO管理器');

    // this.saveData();

    log.info('初始化消息服务');
    this.canvas.renderAll();
    this._cacheable = false;
    // 把eventemitter 当作静态实例的时候，会导致多实例sdk 共享emitter，会发生覆盖
    // 有时间进行优化
    this.sender = new Sender();
    this.receiver = new Receiver();
    this.receiver.app = this;

    this.items = new Items();
    this.items.init(this.canvas);

    this.items.app = this;
    this.items.addEvents();
    this.items.cache = this.cache;

    this.graphicFactory = new GraphicFactory(this);

    log.info('[Doodle] init cost end');
  }

  currentGraphic(id) {
    if (!this._mouseEnable) {
      id = -1;
    }
    // ToolsManager.getInstance().currentGraphic = parseInt(id)
    this.tools.currentGraphic = +id;
  }

  getCurrentGraphicId() {
    if (!this._mouseEnable) {
      return -1;
    }
    return this.tools.currentGraphicId;
  }

  set option(opt) {
    if (!this._mouseEnable) {
      return;
    }
    this.tools.option = opt;
  }

  get option() {
    return this.tools.option;
  }

  get version() {
    return process.env.CORE_VERSION;
  }

  currentTool(id) {
    // 3 撤销 4重做
    if (this._mouseEnable || id === 3 || id === 4) {
      this.tools.currentTool = +id;
    }
  }

  trigger(action, data) {
    this.receiver.emitter.emit(action, data);
    this.canvas.renderAll();
  }

  on(act, handler) {
    this.sender.emitter.on(act, handler);
    this.canvas.renderAll();
  }

  off(act, handler) {
    this.sender.emitter.removeListener(act, handler);
  }

  offAll() {
    this.sender.emitter.removeAllListeners();
  }

  /**
   * 获取当前canvas的所有画笔数据
   *
   * @returns
   * @memberof Doodle
   */
  getAllData() {
    this.stage.renderNow();
    return this.canvas.toDatalessJSON();
  }

  /**
   *
   *  设置当前画布所有数据
   * @param {*} value
   * @memberof Doodle
   */
  setAllData(value) {
    this.canvas.loadFromDatalessJSON(value);

    // 如果有水印，设置水印
    if (DocModuler.watermarkOption.text) {
      this.setWatermark();
    }
    this.stage.renderNow();
  }

  fitToOrigin(w, h) {
    log.warn('即将废弃的方法，请使用setCanvasSize 替代');
    // 与远端画布大小同步
    this.canvas.setDimensions({
      width: w,
      height: h
    });

    this.stage.renderNow();
  }

  setSize(w, h) {
    this.canvas.setDimensions({
      width: w,
      height: h
    });

    const oldW = this.store.getKV('width') || this.store.getKV('ow');
    let ratio = w / oldW;

    this.canvas.zoomToPoint(
      {
        x: 0,
        y: 0
      },
      ratio
    );
    setTimeout(() => {
      if (this.canvas.overlayImage) {
        this.canvas.overlayImage.scale(1 / ratio);
        this.canvas.overlayImage.viewportCenter();
      }
    }, 50);
    this.stage.renderNow();
  }

  setSizeByDraw(w, h) {
    log.warn('即将废弃的方法，请使用setSize 替代');
    let oldW = this.canvas.width;
    this.canvas.setDimensions({
      width: w,
      height: h
    });

    let ratio = w / oldW;
    this.canvas.zoomToPoint(
      {
        x: 0,
        y: 0
      },
      ratio
    );

    this.stage.renderNow();
  }

  setCanvasSize(w, h) {
    // 这里设置的是Canvas内部绘图缓冲区的宽高,而不是 DOM 元素的显示宽高
    const otherWidth = this.store.getKV('width');
    const otherHeight = this.store.getKV('height');
    if (!otherWidth || !otherHeight) {
      this.store.setKV('width', w);
      this.store.setKV('height', h);
    }
    if (w !== this.lastCanvasSize.width || h !== this.lastCanvasSize.height) {
      this.canvas.setWidth(w);
      this.canvas.setHeight(h);
      this.canvas.calcOffset();
      this.stage.renderNow();
      this.lastCanvasSize = {
        width: w,
        height: h
      };
    }
  }

  setBackgroundColor(val) {
    this.canvas.backgroundColor = val;
    this.stage.renderNow();
  }

  deselectAll() {
    this.stage.deselectAll();
  }

  /**
   * 设置当前页码
   *
   * @memberof Doodle
   */
  set currentId(id) {
    log.info(`设置当前页码: ${id}`);
    log.info(`_currentId: ${this._currentId}`);
    if (this._currentId !== id) {
      setTimeout(() => {
        this.clearData();
        this.saveData();
      }, 1000);
    }

    if (this._currentId === id) {
      return;
    }

    // 临时保存当前选择的涂鸦类型
    let currentGraphicId = this.getCurrentGraphicId();

    // 切页的时候当前页存起来
    if (this._currentId) {
      this.cache.setCache(this._currentId, this.canvas.toDatalessJSON());
      this.currentGraphic(-1);
    }
    this._currentId = id;
    this.store.setKV('pageId', id);
    let data = this.cache.getCache(id);
    if (data) {
      // this.canvas.off(CanvasEventType.Add)
      this.setAllData(data);
      setObjectDefaultControlOption(this.canvas, this.tools.defaultControlOption, this);
      UndoManager.getInstance().save(data);
      // this.canvas.on(CanvasEventType.Add)
    }

    // 恢复当前涂鸦类型，默认画笔类型
    this.currentGraphic(currentGraphicId >= 1 ? currentGraphicId : -1);

    // this.mouseEnable(this._mouseEnable)
  }

  set cacheable(v) {
    this._cacheable = v;
    this.cache.cacheable = v;
  }

  get currentId() {
    return this._currentId;
  }

  clearCache() {
    this.cache.clear();
  }

  clearCacheById(key) {
    this.cache.removeCache(key);
  }

  clear() {
    // 调用clear 会将背景颜色也同时清空
    // this.canvas.clear()
    this.canvas.remove(...this.canvas.getObjects());
    this.canvas.renderAll();
  }

  destroy() {
    let cvs = window.canvas;
    if (cvs && cvs.indexOf(this.canvas > -1)) {
      cvs.splice(this.canvas, 1);
    }

    this.currentTool(-1);
    if (this.canvas) {
      this.canvas.off();
      this.canvas.removeListeners();
      this.canvas.dispose();
      this.canvas = null;
    }
  }

  /**
   * 设置涂鸦是否接收鼠标事件
   *
   * @param {*} b
   * @memberof Doodle
   */
  mouseEnable(b) {
    this._mouseEnable = b;
    this.store.setKV('mouseEnable', b);
    this.currentGraphic(-1);
    this.stage.deselectAll();
    setObjectCanSelect(this.canvas, b);
    this.stage.renderNow();
  }

  setControlOption(style) {
    if (!this.tools) {
      return;
    }

    this.tools.defaultControlOption = style;
    setObjectDefaultControlOption(this.canvas, this.tools.defaultControlOption, this);
  }

  restore() {
    if (!this.canvas) {
      log.warn('未初始化，无法还原');
      return;
    }
    // part 1: 基于左上角缩放成为1
    this.canvas.zoomToPoint(
      {
        x: 0,
        y: 0
      },
      1
    );
    // part 2: 调整视角（舞台位置）
    this.canvas.viewportTransform[4] = 0;
    this.canvas.viewportTransform[5] = 0;
    this.canvas.requestRenderAll();
  }
  /**
   * 创建水印
   */
  async setWatermark() {
    deferQueue.push('', this.setWatermarkQueue.bind(this));
  }

  async setWatermarkQueue() {
    log.info('window.canvas[0] === this.canvas:', window.canvas[0] === this.canvas);
    try {
      const width = this.canvas?.width || this.store.getKV('ow');
      const height = this.canvas?.height || this.store.getKV('oh');

      if (!width || !height) return;

      // 每次翻页的时候会清空cavans，所以需要重新绘制。缓存水印图片，防止重复生成
      if (!DocModuler.watermarkImg) {
        DocModuler.watermarkImg = await setWatermark(width, height);
        log.info('获取水印图片');
      }

      const { angle, opacity } = DocModuler.watermarkOption;
      let { scaleX, scaleY } = fabric.util.qrDecompose(this.canvas.viewportTransform);
      this.canvas.setOverlayImage(
        DocModuler.watermarkImg,
        () => {
          this.canvas.overlayImage.viewportCenter();
        },
        {
          opacity,
          angle: -`${angle}`,
          scaleX: 1 / scaleX,
          scaleY: 1 / scaleY,
          originX: 'left',
          originY: 'top',
          excludeFromExport: true
        }
      );
    } catch (e) {
      log.error('水印异常');
      log.error(e);
    }
  }

  /**
   * 清除水印
   */
  clearWatermark() {
    log.info('=水印=clearWatermark====');
    DocModuler.watermarkImg = null;
    this.canvas.overlayImage = null;
    this.canvas.renderAll();
  }
  saveData() {
    let data = this.canvas.toDatalessJSON();
    UndoManager.getInstance().save(data);
  }
  clearData() {
    UndoManager.getInstance().clear();
  }

  // 导入绘制数据
  loadManydrawing(arr, size) {
    if (!arr || arr.length <= 0) return;

    this.canvas.remove(...this.canvas.getObjects());
    const originBindArrAdd = this.canvas.__eventListeners[CanvasEventType.Add];
    const originBindArrModify = this.canvas.__eventListeners[CanvasEventType.Modified];
    // 重置为 undefined 不让 object:adaded 事件执行，因为后面 add 数据会触发该事件
    this.canvas.__eventListeners[CanvasEventType.Add] = undefined;
    this.canvas.__eventListeners[CanvasEventType.Modified] = undefined;
    // // 添加数据到 canvas
    for (const item of arr) {
      console.log('afterFlipResult item:', item.opType);
      if (item.opType === '*add') {
        const obj = this.graphicFactory.createByData(item);
        this.canvas.add(obj);
      } else if (item.opType === '*modify') {
        this.graphicFactory.modifyByData(item);
      }
    }
    // // 还原回去
    if (originBindArrAdd) {
      this.canvas.__eventListeners[CanvasEventType.Add] = originBindArrAdd;
    }
    if (originBindArrModify) {
      this.canvas.__eventListeners[CanvasEventType.Modified] = originBindArrModify;
    }

    this.canvas.renderAll();
    if (size && size.cw > 0 && size.ch > 0) {
      this.setSize(size.cw, size.ch);
    }
  }
}
